import { Link } from "gatsby";
import React, { useEffect, useRef } from "react";
import { AiOutlineClose, AiOutlineMenu } from "react-icons/ai";
import { HiMagnifyingGlass } from "react-icons/hi2";
import styled from "styled-components";
import Logo from "../../images/icons/logo.svg";
import { ButtonLink } from "../ButtonLink/ButtonLink";
import { HeaderProps } from "./Header";

const StyledHeader = styled.header`
  display: flex;
  align-items: center;
  background: var(--colour-dark-grey);
  height: 70px;
  width: 100%;

  .logo {
    fill: var(--colour-yellow);
    width: 46px;
    height: 46px;
  }
`;

const StyledMobileMenu = styled.div`
  position: fixed;
  top: 70px;
  right: 0;
  bottom: 0;
  left: 0;
  overflow-y: auto;
  z-index: 999;

  padding: 2rem 0;

  background-color: #d9d9d9;
`;

const Container = styled.div`
  max-width: var(--layout-max-content-width);
  margin: 0 auto;
  width: 100%;
  padding-left: 1rem;

  display: flex;
  align-items: center;
`;

const StyledMenuButton = styled.button`
  background-color: transparent;
  border: none;
  height: 70px;
  width: 70px;
  cursor: pointer;

  svg {
    fill: var(--colour-yellow);
    width: 40px;
    height: 40px;
  }
`;

const StyledList = styled.ul<{ $mainMenu?: boolean }>`
  margin: 0;
  padding: 0;
  list-style: none;

  h2 {
    margin: 0;
    padding: 1rem;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.1;
    color: var(--colour-dark-grey);
  }

  a,
  button {
    border: 0;
    text-align: left;
    background-color: transparent;
    display: block;
    padding: 0.75rem 2.1rem;
    width: 100%;
    text-decoration: none;
    font-size: 20px;
    color: var(--colour-dark-grey);
    cursor: pointer;

    ${(props) => props.$mainMenu && "font-size: 22px;"}
    ${(props) => props.$mainMenu && "font-weight: 700;"}
    ${(props) => props.$mainMenu && "line-height: 1.1;"}

    &:hover {
      text-decoration: underline;
    }
  }

  button {
    padding: 0.75rem 1rem;
  }
`;

const StyledButtonContainer = styled.div`
  padding: 1rem;
`;

export const SearchIconLink = styled(Link)`
  color: var(--colour-white);
  margin-left: auto;

  svg {
    width: 32px;
    height: 32px;
  }
`;

export const MobileHeader: React.FC<HeaderProps> = ({ navItems }) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [activeSubMenu, setActiveSubMenu] = React.useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const subMenuRef = useRef<HTMLDivElement>(null);
  const subMenuButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";

      // Focus trap logic
      const focusableSelectors =
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
      const menuNode = menuRef.current;
      const focusableEls = menuNode
        ? Array.from(menuNode.querySelectorAll<HTMLElement>(focusableSelectors))
        : [];
      const firstEl = focusableEls[0];
      const lastEl = focusableEls[focusableEls.length - 1];

      // Focus the first element
      firstEl?.focus();

      function handleKeyDown(e: KeyboardEvent) {
        if (e.key === "Tab") {
          if (focusableEls.length === 0) return;
          if (e.shiftKey) {
            // Shift + Tab
            if (document.activeElement === firstEl) {
              e.preventDefault();
              lastEl.focus();
            }
          } else {
            // Tab
            if (document.activeElement === lastEl) {
              e.preventDefault();
              firstEl.focus();
            }
          }
        }
        if (e.key === "Escape") {
          setIsMenuOpen(false);
        }
      }

      menuNode?.addEventListener("keydown", handleKeyDown);

      return () => {
        document.body.style.overflow = "auto";
        menuNode?.removeEventListener("keydown", handleKeyDown);
      };
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isMenuOpen]);

  // Focus first link in sub menu when it opens
  useEffect(() => {
    if (activeSubMenu !== null && subMenuRef.current) {
      const firstLink = subMenuRef.current.querySelector<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      firstLink?.focus();
    } else if (activeSubMenu === null && subMenuButtonRef.current) {
      subMenuButtonRef.current.focus();
    }
  }, [activeSubMenu]);

  return (
    <>
      <Container>
        <Link
          to="/"
          aria-label="Answer"
          onClick={() => {
            setIsMenuOpen(false);
            setActiveSubMenu(null);
          }}
        >
          <Logo className="logo" />
        </Link>
        <SearchIconLink to="/search" aria-label="Search">
          <HiMagnifyingGlass />
        </SearchIconLink>
        <StyledMenuButton
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          onClick={() => {
            setIsMenuOpen(!isMenuOpen);
            setActiveSubMenu(null);
          }}
        >
          {isMenuOpen ? <AiOutlineClose /> : <AiOutlineMenu />}
        </StyledMenuButton>
      </Container>
      {isMenuOpen && (
        <StyledMobileMenu ref={menuRef} tabIndex={-1} aria-modal="true" role="dialog">
          <StyledList $mainMenu>
            {navItems.map((navItem, index) => (
              <li key={navItem._key}>
                {navItem.subNavigation ? (
                  <button
                    ref={(el) => {
                      // Always keep the last clicked button in the ref
                      if (el && activeSubMenu === index) {
                        subMenuButtonRef.current = el;
                      }
                    }}
                    onClick={(e) => {
                      subMenuButtonRef.current = e.currentTarget;
                      setActiveSubMenu(index);
                    }}
                  >
                    &gt; {navItem.title}
                  </button>
                ) : (
                  <Link
                    onClick={() => {
                      setActiveSubMenu(null);
                      setIsMenuOpen(false);
                    }}
                    to={
                      navItem.internalLink?.slug.current
                        ? `/${navItem.internalLink?.slug.current}`
                        : navItem.externalLink || navItem.customLink
                    }
                  >
                    &gt; {navItem.title}
                  </Link>
                )}
              </li>
            ))}
          </StyledList>
          {activeSubMenu !== null && (
            <StyledMobileMenu ref={subMenuRef}>
              <StyledList>
                <li>
                  <button onClick={() => setActiveSubMenu(null)}>
                    &lt; {navItems[activeSubMenu].title}
                  </button>
                </li>
                {navItems[activeSubMenu].subNavigation?.columns?.map((column) => (
                  <React.Fragment key={column._key}>
                    {column.title && <h2>&gt; {column.title}</h2>}
                    {column.links.map((link) => (
                      <li key={link._key}>
                        <Link
                          to={
                            link.internalLink?.slug.current
                              ? `/${link.internalLink?.slug.current}`
                              : link.externalLink || link.customLink
                          }
                          onClick={() => {
                            setActiveSubMenu(null);
                            setIsMenuOpen(false);
                          }}
                        >
                          {link.title}
                        </Link>
                      </li>
                    ))}
                  </React.Fragment>
                ))}
              </StyledList>
            </StyledMobileMenu>
          )}
          <StyledButtonContainer>
            <ButtonLink
              link={{
                link: "/contact-us",
                linkVariant: "primary",
                linkInverse: false,
                linkTitle: "Book a consultation",
              }}
              variant="primary"
              onClick={() => setIsMenuOpen(false)}
            >
              Book a consultation
            </ButtonLink>
          </StyledButtonContainer>
        </StyledMobileMenu>
      )}
    </>
  );
};
